import { Component, HostListener, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ButtonPadraoComponent } from '../../../../shared/components/button-padrao/button-padrao.component';
import { FormFieldComponent } from '../../../../shared/components/form-field/form-field.component';
import { BlocoEmailEditorComponent } from '../bloco-email-editor/bloco-email-editor.component';
import { EmailPreviewComponent } from '../email-preview/email-preview.component';
import { BlocoEmail, ModeloEmail, TipoBlocoEmail, VariavelEmail } from '../../../../shared/model/email/modelo-email.model';
import { criarBloco, montarHtmlEmail, substituirVariaveisEmail } from '../../../../shared/util/montar-html-email';
import { MsgService } from '../../../../shared/service/msg.service';
import { ValidacaoAlteracaoCampoService } from '../../../../shared/service/utilitarios/validacao-alteracao-campo.service';

export interface DadosMontadorEmail {
  modelo?: ModeloEmail;
  somenteLeitura?: boolean;
  variaveis?: VariavelEmail[];
}

interface ItemPaleta {
  tipo: TipoBlocoEmail;
  icone: string;
  rotulo: string;
}

@Component({
  selector: 'app-montador-email',
  standalone: true,
  imports: [
    MatIconModule,
    DragDropModule,
    ButtonPadraoComponent,
    FormFieldComponent,
    BlocoEmailEditorComponent,
    EmailPreviewComponent
  ],
  templateUrl: './montador-email.component.html',
  styleUrl: './montador-email.component.css'
})
export class MontadorEmailComponent implements OnInit {
  modelo!: ModeloEmail;
  somenteLeitura!: boolean;
  htmlPreview = '';
  blocoPreviewId?: string;
  variaveis!: VariavelEmail[];

  readonly paleta: ItemPaleta[] = [
    { tipo: 'TITULO', icone: 'title', rotulo: 'Título' },
    { tipo: 'TEXTO', icone: 'notes', rotulo: 'Texto' },
    { tipo: 'IMAGEM', icone: 'image', rotulo: 'Imagem' },
    { tipo: 'BOTAO', icone: 'smart_button', rotulo: 'Botão' },
    { tipo: 'VARIAVEL', icone: 'data_object', rotulo: 'Variável' },
    { tipo: 'DIVISOR', icone: 'horizontal_rule', rotulo: 'Divisor' },
    { tipo: 'HTML', icone: 'code', rotulo: 'HTML colado' }
  ];

  private readonly dialogRef = inject<MatDialogRef<MontadorEmailComponent, ModeloEmail>>(MatDialogRef);
  private readonly msgService = inject(MsgService);
  private readonly alteracaoService = inject(ValidacaoAlteracaoCampoService);
  private readonly data = inject<DadosMontadorEmail>(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.modelo = this.clonarModelo(this.data?.modelo);
    this.somenteLeitura = !!this.data?.somenteLeitura;
    this.variaveis = this.data?.variaveis ?? [];
    this.recalcular();
    this.alteracaoService.camposNaoAlterados();
  }

  private marcarAlteracao(): void {
    if (!this.somenteLeitura) {
      this.alteracaoService.camposAlterado();
    }
  }

  aoAlterarBloco(bloco: BlocoEmail): void {
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  private clonarModelo(origem?: ModeloEmail): ModeloEmail {
    const modelo = new ModeloEmail();
    modelo.assunto = origem?.assunto ?? '';
    modelo.blocos = (origem?.blocos ?? []).map(b => ({ ...b }));
    return modelo;
  }

  recalcular(): void {
    this.htmlPreview = montarHtmlEmail(this.modelo, this.variaveis, true);
  }

  get assuntoPreview(): string {
    return substituirVariaveisEmail(this.modelo.assunto, this.variaveis);
  }

  itemDesabilitado(item: ItemPaleta): boolean {
    return this.somenteLeitura || (item.tipo === 'VARIAVEL' && !this.variaveis.length);
  }

  atualizarAssunto(valor: string): void {
    this.blocoPreviewId = undefined;
    this.modelo.assunto = valor;
    this.recalcular();
    this.marcarAlteracao();
  }

  adicionarBloco(tipo: TipoBlocoEmail): void {
    if (this.somenteLeitura || (tipo === 'VARIAVEL' && !this.variaveis.length)) {
      return;
    }
    const bloco = criarBloco(tipo);
    if (tipo === 'VARIAVEL') {
      bloco.texto = this.variaveis[0].token;
    }
    this.modelo.blocos = [...this.modelo.blocos, bloco];
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  removerBloco(indice: number): void {
    this.modelo.blocos.splice(indice, 1);
    this.modelo.blocos = [...this.modelo.blocos];
    this.blocoPreviewId = this.modelo.blocos[Math.min(indice, this.modelo.blocos.length - 1)]?.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  subir(indice: number): void {
    if (indice <= 0) {
      return;
    }
    const bloco = this.modelo.blocos[indice];
    moveItemInArray(this.modelo.blocos, indice, indice - 1);
    this.modelo.blocos = [...this.modelo.blocos];
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  descer(indice: number): void {
    if (indice >= this.modelo.blocos.length - 1) {
      return;
    }
    const bloco = this.modelo.blocos[indice];
    moveItemInArray(this.modelo.blocos, indice, indice + 1);
    this.modelo.blocos = [...this.modelo.blocos];
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  moverTopo(indice: number): void {
    if (indice <= 0) {
      return;
    }
    const bloco = this.modelo.blocos[indice];
    moveItemInArray(this.modelo.blocos, indice, 0);
    this.modelo.blocos = [...this.modelo.blocos];
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  moverFundo(indice: number): void {
    if (indice >= this.modelo.blocos.length - 1) {
      return;
    }
    const bloco = this.modelo.blocos[indice];
    moveItemInArray(this.modelo.blocos, indice, this.modelo.blocos.length - 1);
    this.modelo.blocos = [...this.modelo.blocos];
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  onReordenarBlocos(event: CdkDragDrop<BlocoEmail[]>): void {
    if (this.somenteLeitura || event.previousIndex === event.currentIndex) {
      return;
    }
    const bloco = this.modelo.blocos[event.previousIndex];
    moveItemInArray(this.modelo.blocos, event.previousIndex, event.currentIndex);
    this.modelo.blocos = [...this.modelo.blocos];
    this.blocoPreviewId = bloco.id;
    this.recalcular();
    this.marcarAlteracao();
  }

  @HostListener('document:keydown.enter', ['$event'])
  aoPressionarEnter(event: KeyboardEvent): void {
    if (this.somenteLeitura) {
      return;
    }
    const alvo = event.target as HTMLElement;
    const dentroDoAssunto = !!alvo.closest?.('.campo-assunto-email');
    if (dentroDoAssunto) {
      event.preventDefault();
      this.salvar();
      return;
    }
    const dentroDeCampoDeTexto = !!alvo.closest?.(
      'input, textarea, [contenteditable="true"], mat-select, .mat-mdc-select'
    );
    if (dentroDeCampoDeTexto) {
      return;
    }
    event.preventDefault();
    this.salvar();
  }

  @HostListener('document:keydown.escape', ['$event'])
  aoPressionarEsc(event: KeyboardEvent): void {
    event.preventDefault();
    this.cancelar();
  }

  salvar(): void {
    if (!this.modelo.assunto?.trim()) {
      this.msgService.msgAdvertencia('Informe o assunto do e-mail.');
      return;
    }
    if (!this.modelo.blocos.length) {
      this.msgService.msgAdvertencia('Adicione ao menos um bloco ao e-mail.');
      return;
    }
    this.alteracaoService.camposNaoAlterados();
    this.dialogRef.close(this.modelo);
  }

  cancelar(): void {
    const podeFechar =
      !this.alteracaoService.isCamposAlteracao() ||
      window.confirm('Pode possuir alterações não salvas, deseja fechar?');
    if (!podeFechar) {
      return;
    }

    this.alteracaoService.camposNaoAlterados();
    this.dialogRef.close();
  }
}
